<template>
  <div v-if="hasMultipleTabs" id="carouselTabsContainer">
    <CarouselTab
      v-if="hasFixedTab"
      class="sticky-tab"
      :details="fixedTab"
      :width="tabWidth"
      :height="tabHeight"
      :data-test="`tab-${fixedTab.path}`"
    />
    <transition name="fade">
      <div v-if="!scrolledToStart" class="scroll-button-container back" @click="scrollBack">
        <div class="scroll-button-inner"><div class="scroll-button-label">&lt;</div></div>
      </div>
    </transition>
    <div ref="scrollContainer" class="scroll-container" @scroll="watchScroll">
      <CarouselTab
        v-for="tab of visibleTabs"
        :key="`tab-${tab.secondaryAppPath || tab.nodeType}-${tab.path}`"
        :details="tab"
        :width="tabWidth"
        :height="tabHeight"
        :data-test="`tab-${tab.path}`"
      />
    </div>
    <transition name="fade">
      <div v-if="!scrolledToEnd" class="scroll-button-container forward" @click="scrollForward">
        <div class="scroll-button-inner"><div class="scroll-button-label">&gt;</div></div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'
import CarouselTab from '@/components/public/CarouselTabs/CarouselTab.vue'
import { NodeTypes, Tab } from '@/types'

const components = {
  CarouselTab,
}

const props = {}

// eslint-disable-next-line no-use-before-define
@Component<CarouselTabs>({
  components,
  watch: {
    activeTab() {
      this.$nextTick(this.scrollToActiveTab)
    },
  },
})
export default class CarouselTabs extends Vue.extend({ props }) {
  scrolledToEnd = false
  scrolledToStart = true
  tabHeight: number | undefined = 0
  tabWidth: number | undefined = 0
  resizeObserver: ResizeObserver | null = null

  async mounted() {
    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(this.$el as HTMLElement)
    this.handleResize()
  }

  async updated() {
    this.handleResize()
  }

  beforeDestroy() {
    this.resizeObserver && this.resizeObserver.disconnect()
  }

  handleResize() {
    const minTabHeight = 60
    const minTabWidth = 90
    this.$nextTick(() => {
      const { clientHeight, clientWidth } = this.$el
      this.tabHeight = this.isLandscape
        ? Math.max(minTabHeight, clientHeight / this.allTabs.length)
        : undefined
      this.tabWidth = !this.isLandscape
        ? Math.max(minTabWidth, clientWidth / this.allTabs.length)
        : undefined
      this.watchScroll()
    })
  }

  get activeTab() {
    return this.$typedStore.public.display.activeTab
  }

  get isLandscape() {
    return this.$typedStore.public.display.isLandscape
  }

  get hasMultipleTabs() {
    return this.visibleTabs.length > 1
  }

  get allTabs() {
    return this.$typedStore.primary.entities.tabs.visibleTabs
  }

  get hasFixedTab() {
    return this.allTabs[0]?.nodeType === NodeTypes.ROOT
  }

  get fixedTab() {
    return this.allTabs[0]
  }

  get visibleTabs() {
    return this.allTabs.slice(this.hasFixedTab ? 1 : 0)
  }

  get scrollContainer() {
    return this.$refs?.scrollContainer as Element
  }

  scrollToActiveTab() {
    const activeTab = this.scrollContainer?.querySelector('.active')
    if (!activeTab) {
      return
    }
    if (
      this.isPartiallyOrCompletelyHidden(activeTab, 'left') ||
      this.isPartiallyOrCompletelyHidden(activeTab, 'top')
    ) {
      activeTab.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else if (
      this.isPartiallyOrCompletelyHidden(activeTab, 'right') ||
      this.isPartiallyOrCompletelyHidden(activeTab, 'bottom')
    ) {
      activeTab.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
  }

  watchScroll() {
    const { scrollLeft, scrollWidth, clientWidth, scrollTop, scrollHeight, clientHeight } =
      this.scrollContainer
    const buffer = 0.5
    if (this.isLandscape) {
      this.scrolledToStart = scrollTop === 0
      this.scrolledToEnd = scrollTop + clientHeight + buffer >= scrollHeight
    } else {
      this.scrolledToStart = scrollLeft === 0
      this.scrolledToEnd = scrollLeft + clientWidth + buffer >= scrollWidth
    }
  }

  scrollBack() {
    const tabs = this.scrollContainer.children
    for (let i = tabs.length - 1; i >= 0; i--) {
      const tab = tabs[i]
      if (this.isPartiallyOrCompletelyHidden(tab, this.isLandscape ? 'top' : 'left')) {
        tab.scrollIntoView({ behavior: 'smooth', block: 'start' })
        break
      }
    }
  }

  scrollForward() {
    const tabs = this.scrollContainer.children
    for (let i = 0; i < tabs.length; i++) {
      const tab = tabs[i]
      if (this.isPartiallyOrCompletelyHidden(tab, this.isLandscape ? 'bottom' : 'right')) {
        tab.scrollIntoView({ behavior: 'smooth', block: 'end' })
        break
      }
    }
  }

  isPartiallyOrCompletelyHidden(
    tab: Element,
    direction: 'left' | 'right' | 'top' | 'bottom',
  ): boolean {
    const containerRect = this.scrollContainer.getBoundingClientRect()
    const tabRect = tab.getBoundingClientRect()
    const pad = 10
    if (direction === 'left' || direction === 'top') {
      return containerRect[direction] - tabRect[direction] > pad
    }
    return tabRect[direction] - containerRect[direction] > pad
  }

  displaySubtabs(tab: Tab) {
    return (
      tab.nodeType === NodeTypes.APP && tab.path === this.$typedStore.public.display.activeTab?.path
    )
  }
}
</script>

<style lang="scss" scoped>
#carouselTabsContainer {
  z-index: 1;
  display: flex;
  .scroll-button-container {
    position: relative;
    transition: opacity 0.2s;
    &.fade-enter-from,
    &.fade-leave-to {
      opacity: 0;
    }
    .scroll-button-inner {
      display: flex;
      justify-content: center;
      align-items: center;
      overflow: hidden;
      background: rgba(1, 1, 1, 0.7);
      .scroll-button-label {
        color: white;
        font-weight: 400;
        font-size: 42px;
      }
    }
  }
  .scroll-container {
    flex: 1;
    min-width: 0;
    min-height: 0;
    overflow-x: scroll;
    overflow-y: hidden;
    flex-wrap: nowrap;
    display: flex;
  }
  @media (orientation: portrait) {
    width: 100%;
    height: 60px;
    .scroll-button-container {
      height: 100%;
      width: 0;
      &.forward {
        left: -15px;
      }
      .scroll-button-inner {
        width: 15px;
        height: 100%;
        .scroll-button-label {
          transform: scaleX(0.3);
          padding-bottom: 4px;
        }
      }
    }
  }
  @media (orientation: landscape) {
    height: 100%;
    flex-direction: column;
    .scroll-container {
      flex-direction: column;
      overflow-x: hidden;
      overflow-y: scroll;
    }
    .scroll-button-container {
      height: 0;
      width: 100%;
      &.forward {
        top: -15px;
      }
      .scroll-button-inner {
        height: 15px;
        width: 100%;
        .scroll-button-label {
          transform: rotateZ(90deg) scaleX(0.3);
        }
      }
    }
  }
  .tab-container {
    margin: 0;
    padding: 0;
    box-shadow: none;
    &.active {
      box-shadow: -0.5vw 0.15vh 1vh #000;
    }
    &.subtab {
      box-sizing: border-box;
      border-top: 1px solid #999 !important;
    }
    .tab-inner {
      padding-left: 0.75vh;
      flex-direction: column;
      justify-content: center;
      padding: 0 1.5vh;
      vertical-align: middle;
      .tab-label {
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }
    }
    .sub-tab-border {
      border-right: solid;
      z-index: 2;
      width: 0;
      transition: width 1s;
      pointer-events: none;
    }

    .tab-label {
      text-align: center;
    }
  }
}
</style>
